import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

interface PreviewError {
  kind: 'permission-denied' | 'not-found' | 'unknown';
  message: string;
}

interface LocalMediaPreview {
  videoStream: MediaStream | null;
  audioStream: MediaStream | null;
  audioLevel: number;
  devices: { cameras: MediaDeviceInfo[]; microphones: MediaDeviceInfo[] };
  error: PreviewError | null;
  cameraId: string | undefined;
  micId: string | undefined;
  setCameraId: (id: string | undefined) => void;
  setMicId: (id: string | undefined) => void;
  /**
   * Whether the camera / mic tracks are enabled on the preview stream.
   * Toggling these flips `track.enabled` on the live tracks — the stream
   * stays alive (cheap to flip back without a fresh getUserMedia). The
   * caller surfaces these in the join payload so LiveKit comes up with
   * the publish state the user picked in the lobby.
   */
  cameraEnabled: boolean;
  micEnabled: boolean;
  setCameraEnabled: (enabled: boolean) => void;
  setMicEnabled: (enabled: boolean) => void;
  retry: () => void;
}

interface UseLocalMediaPreviewArgs {
  mode: 'online' | 'helpline';
}

/**
 * Pre-LiveKit media preview. Owns a getUserMedia stream and an
 * enumerateDevices list so pre-join can render a camera preview tile,
 * audio level meter, and device dropdowns. Mode 'online' requests
 * video + audio; 'helpline' requests audio only. Tears down tracks on
 * unmount OR when the consumer swaps device IDs mid-render.
 */
export function useLocalMediaPreview({ mode }: UseLocalMediaPreviewArgs): LocalMediaPreview {
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [devices, setDevices] = useState({
    cameras: [] as MediaDeviceInfo[],
    microphones: [] as MediaDeviceInfo[],
  });
  const [error, setError] = useState<PreviewError | null>(null);
  const [cameraId, setCameraId] = useState<string | undefined>(undefined);
  const [micId, setMicId] = useState<string | undefined>(undefined);
  const [cameraEnabled, setCameraEnabledState] = useState(true);
  const [micEnabled, setMicEnabledState] = useState(true);

  // retryCount is incremented by retry() to re-trigger the getUserMedia effect.
  // useReducer gives a stable dispatch that does not need to appear in deps.
  const [retryCount, incrementRetry] = useReducer((n: number) => n + 1, 0);

  const previousStreamsRef = useRef<MediaStream[]>([]);

  const retry = useCallback(() => {
    setError(null);
    incrementRetry();
  }, []);

  // Flip `track.enabled` on the active video tracks. enabled=false sends
  // black frames downstream (LiveKit will skip publishing when the join
  // payload says video=false anyway, but flipping here also blanks the
  // local <video> preview the moment the user toggles).
  const setCameraEnabled = useCallback((enabled: boolean) => {
    setCameraEnabledState(enabled);
    for (const stream of previousStreamsRef.current) {
      for (const t of stream.getVideoTracks()) t.enabled = enabled;
    }
  }, []);

  const setMicEnabled = useCallback((enabled: boolean) => {
    setMicEnabledState(enabled);
    for (const stream of previousStreamsRef.current) {
      for (const t of stream.getAudioTracks()) t.enabled = enabled;
    }
  }, []);

  // Acquire streams + enumerate devices.
  useEffect(() => {
    let cancelled = false;
    const constraints: MediaStreamConstraints = {
      video: mode === 'online' ? (cameraId ? { deviceId: cameraId } : true) : false,
      audio: micId ? { deviceId: micId } : true,
    };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(async (stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => {
            t.stop();
          });
          return;
        }
        // Stop any previous streams.
        previousStreamsRef.current.forEach((s) => {
          s.getTracks().forEach((t) => {
            t.stop();
          });
        });
        previousStreamsRef.current = [stream];

        const videoTracks = stream.getVideoTracks();
        const audioTracks = stream.getAudioTracks();
        // Apply the current enabled flags to freshly acquired tracks so a
        // device swap mid-lobby preserves the user's mute/blank choice.
        for (const t of videoTracks) t.enabled = cameraEnabled;
        for (const t of audioTracks) t.enabled = micEnabled;
        setVideoStream(videoTracks.length > 0 ? new MediaStream(videoTracks) : null);
        setAudioStream(audioTracks.length > 0 ? new MediaStream(audioTracks) : null);

        try {
          const enumerated = await navigator.mediaDevices.enumerateDevices();
          setDevices({
            cameras: enumerated.filter((d) => d.kind === 'videoinput'),
            microphones: enumerated.filter((d) => d.kind === 'audioinput'),
          });
        } catch {
          // enumerateDevices can fail on some browsers; non-fatal.
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : '';
        const message = err instanceof Error ? err.message : 'Unknown error';
        const kind: PreviewError['kind'] =
          name === 'NotAllowedError'
            ? 'permission-denied'
            : name === 'NotFoundError' || name === 'OverconstrainedError'
              ? 'not-found'
              : 'unknown';
        setError({ kind, message });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cameraEnabled/micEnabled only stamp freshly acquired tracks; as deps every lobby toggle would re-run getUserMedia (toggles flip track.enabled via setCameraEnabled/setMicEnabled instead)
  }, [mode, cameraId, micId, retryCount]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      previousStreamsRef.current.forEach((s) => {
        s.getTracks().forEach((t) => {
          t.stop();
        });
      });
      previousStreamsRef.current = [];
    };
  }, []);

  // Device-change listener.
  useEffect(() => {
    const mediaDevices = navigator.mediaDevices;
    function handleChange() {
      mediaDevices
        .enumerateDevices()
        .then((enumerated) => {
          setDevices({
            cameras: enumerated.filter((d) => d.kind === 'videoinput'),
            microphones: enumerated.filter((d) => d.kind === 'audioinput'),
          });
        })
        .catch(() => {
          /* non-fatal */
        });
    }
    mediaDevices.addEventListener('devicechange', handleChange);
    return () => {
      mediaDevices.removeEventListener('devicechange', handleChange);
    };
  }, []);

  // Audio level meter via AnalyserNode.
  useEffect(() => {
    if (!audioStream) {
      return;
    }
    let rafId: number;
    let audioContext: AudioContext | null = null;
    try {
      audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(audioStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const buffer = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteTimeDomainData(buffer);
        let max = 0;
        for (const raw of buffer) {
          const val = Math.abs(raw - 128) / 128;
          if (val > max) max = val;
        }
        setAudioLevel(max);
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    } catch {
      // AudioContext might be unavailable in some test environments.
    }

    return () => {
      cancelAnimationFrame(rafId);
      audioContext?.close().catch(() => {
        /* non-fatal */
      });
    };
  }, [audioStream]);

  return {
    videoStream,
    audioStream,
    audioLevel,
    devices,
    error,
    cameraId,
    micId,
    setCameraId,
    setMicId,
    cameraEnabled,
    micEnabled,
    setCameraEnabled,
    setMicEnabled,
    retry,
  };
}
